import React, { useMemo, useRef, useState, useLayoutEffect, useEffect } from "react";
import { Type, Palette, Ruler, Sun, Moon, Plus, X, Upload, Move, Maximize2, Layers, RotateCcw, Link2, Link2Off } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useComposition } from "../contexts/CompositionContext";
import GlobalPricePanel from "./GlobalPricePanel";
import CompositionOverlay from "./CompositionOverlay";
import SceneCategoryPicker from "./SceneCategoryPicker";
import MobileDesktopHint from "./MobileDesktopHint";
import { useIsMobile } from "../hooks/useIsMobile";
import ProjectsToolbar from "./ProjectsToolbar";

// ---------- Scènes (facade / angle / interior) ----------
const SCENES = [
  // ─── ANGLE — pharmacies en coin de rue ───
  { id: "corner-artdeco", label: { fr: "Pharmacie d'angle Art Déco" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/gjjfo9f4_3a625685-82df-4367-9aae-3701bc145f83.png", signTop: 38 },
  { id: "corner-dark-metal", label: { fr: "Pharmacie d'angle bardage anthracite" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/70uzpnvr_2922c193-f80f-4c54-bb14-e10941816e5d.png", signTop: 38 },
  { id: "corner-white-metal", label: { fr: "Pharmacie d'angle bardage blanc" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/j6dxj9ny_aea2b1c7-bb18-4ede-9341-e4b76d496474.png", signTop: 36 },
  { id: "corner-haussmann", label: { fr: "Pharmacie d'angle haussmannienne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/ogau3h2u_d9737a23-b900-4a73-b717-c144090e8eca.png", signTop: 56 },
  { id: "corner-modern-cube", label: { fr: "Pharmacie d'angle cube moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/v4rmuot4_fdc965ca-7ab5-48a1-b74e-8ecd4fb0fa46.png", signTop: 32 },
  // ─── FAÇADE — 3 vignettes d'aperçu en premier : immeuble / maison / bâtiment ───
  { id: "facade-modern-white", label: { fr: "Immeuble blanc moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/yf56ryw8_a.png", signTop: 50 },
  { id: "stone-chalet", label: { fr: "Maison chalet pierre" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/s9deqq2q_0f8b84a2-7f8d-41a2-8821-6bea645ff457.png", signTop: 28 },
  { id: "black-cladding", label: { fr: "Bâtiment bardage noir (tôlé)" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/x6apqhu6_84390b14-6569-4b52-abf9-8d12ed5aa6fa.png", signTop: 28 },
  // ─── FAÇADE — Liste complète (déroulant « Voir plus ») ───
  { id: "facade-haussmann", label: { fr: "Façade haussmannienne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/6mhdwjth_b.png", signTop: 56 },
  { id: "facade-classique", label: { fr: "Façade classique parisienne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/7v2fytnw_c.png", signTop: 53 },
  { id: "beige-modern", label: { fr: "Façade beige" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/mi297px7_99ea53b8-9a9d-4271-9c68-d1f363fa4106.png", signTop: 38 },
  { id: "haussmann", label: { fr: "Haussmann" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/js4kaf7b_338b5889-2bea-48ab-98e1-809dfe58103b.png", signTop: 73 },
  { id: "white-bardage", label: { fr: "Bardage blanc" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/qklpcsiy_353fdca0-8632-4559-bd4f-737a5c3f9182.png", signTop: 32 },
  { id: "wood-chalet", label: { fr: "Chalet bois" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/5kogabje_1658e095-2d56-421a-b178-58cab18d78bf.png", signTop: 56 },
  { id: "alpine-pharmacy", label: { fr: "Chalet alpin" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/0k38j41v_36478937-d113-4ddd-824c-5442cb0ac647.png", signTop: 62 },
  { id: "stone-classic", label: { fr: "Pierre classique" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/x2amcont_bd107970-d443-417b-b6e6-0519d1429f35.png", signTop: 67 },
  { id: "cream-haussmann", label: { fr: "Façade crème" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/u7h5rrku_c9dd0a4d-b661-4c3e-844b-c6d7414648fb.png", signTop: 70 },
  { id: "white-modern", label: { fr: "Immeuble blanc" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/bs7nark0_ea3a81f0-d664-43a7-ab34-5061e68a8854.png", signTop: 65 },
  { id: "stone-mountain", label: { fr: "Pierre montagne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/whi8hoxp_f809e244-9835-40d5-9d2e-81ef50d5d803.png", signTop: 28 },
  { id: "white-pharma", label: { fr: "Pharmacie moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/5yqyhz5i_3134e1cd-9628-4a46-a466-f7b7829e6e70.png", signTop: 22 },
  { id: "wood-shopfront", label: { fr: "Devanture bois" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/oipht28q_5831c5f3-234e-425a-b60c-bab4d1648c59.png", signTop: 22 },
  { id: "white-shopfront", label: { fr: "Devanture blanche" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/9g4lmldi_72511b8f-c69c-4389-98e8-657891dac452.png", signTop: 22 },
  // Intérieurs de pharmacie
  { id: "interior-counter-twin", label: { fr: "Intérieur — comptoirs jumeaux" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/f7fa791l_4ac36972-b2fb-4edf-8554-1e28ec6d337d.png", signTop: 40 },
  { id: "interior-counter-boxes", label: { fr: "Intérieur — comptoirs centraux" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/3sblft3b_4c81caa0-8255-48f6-a357-df689f3f69da.png", signTop: 35 },
  { id: "interior-zen-beige", label: { fr: "Intérieur zen beige" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/2acemw6p_7b867c05-e007-4772-bc72-7f8e6e9e1348.png", signTop: 35 },
  { id: "interior-modern-white", label: { fr: "Intérieur blanc moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/tvb6cqqu_8ae97268-566c-4e54-bfd7-2123bc48ea4e.png", signTop: 30 },
  { id: "interior-warm-wood", label: { fr: "Intérieur bois chaleureux" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/23s60spf_69cfc849-6ebe-4424-9a5f-4697212cba4b.png", signTop: 28 },
  { id: "interior-zen-counter-low", label: { fr: "Intérieur zen — comptoir bas" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/xnkzak0u_c5d1ebb2-4b46-4bab-906d-c217253fdbef.png", signTop: 35 },
  { id: "interior-classic-wood", label: { fr: "Intérieur classique bois foncé" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/x53ofjdi_d95e1c7e-03d4-4073-8d8a-36af98029575.png", signTop: 40 },
  { id: "interior-high-ceiling", label: { fr: "Intérieur haut plafond vert" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/0wcgxa83_ddde0c3b-868f-4e87-a675-a24e7d493e37.png", signTop: 28 },
  { id: "interior-luxe-modern", label: { fr: "Intérieur luxe moderne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/12ttou6z_f1282b4f-35b0-4914-81c9-304fd55906cd.png", signTop: 28 },
  { id: "interior-mountain-wood", label: { fr: "Intérieur chalet montagne" }, image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/zpncjwnd_fc5c325a-9c2a-4528-91d9-e6ea43a0f425.png", signTop: 32 },
];

const FONTS = [
  { id: "bebas", label: "Bebas Neue", family: "'Bebas Neue', sans-serif", weight: 400, group: "sans" },
  { id: "anton", label: "Anton", family: "'Anton', sans-serif", weight: 400, group: "sans" },
  { id: "oswald", label: "Oswald", family: "'Oswald', sans-serif", weight: 700, group: "sans" },
  { id: "russoone", label: "Russo One", family: "'Russo One', sans-serif", weight: 400, group: "sans" },
  { id: "audiowide", label: "Audiowide", family: "'Audiowide', display", weight: 400, group: "sans" },
  { id: "montserrat", label: "Montserrat", family: "'Montserrat', sans-serif", weight: 900, group: "sans" },
  { id: "myriad", label: "Myriad Pro", family: "'Source Sans 3', sans-serif", weight: 700, group: "sans" },
  { id: "arial", label: "Arial", family: "Arial, sans-serif", weight: 700, group: "sans" },
  { id: "helvetica", label: "Helvetica", family: "'Inter', sans-serif", weight: 700, group: "sans" },
  { id: "futura", label: "Futura", family: "'Jura', sans-serif", weight: 700, group: "sans" },
  { id: "adlinnaka", label: "Adlinnaka Bold", family: "'Archivo Black', sans-serif", weight: 400, group: "sans" },
  { id: "bahnshrift", label: "Bahnschrift", family: "'Big Shoulders Display', sans-serif", weight: 700, group: "sans" },
  { id: "cabin", label: "Cabin", family: "'Cabin', sans-serif", weight: 700, group: "sans" },
  { id: "akkurat", label: "Akkurat Bold", family: "'Hind', sans-serif", weight: 700, group: "sans" },
  { id: "arcena", label: "Arcena", family: "'Archivo', sans-serif", weight: 700, group: "sans" },
  { id: "avantgarde", label: "Avant Garde", family: "'Jost', sans-serif", weight: 700, group: "sans" },
  { id: "biondi", label: "Biondi Sans", family: "'Saira Condensed', sans-serif", weight: 700, group: "sans" },
  { id: "cinzel", label: "Cinzel", family: "'Cinzel', serif", weight: 700, group: "serif" },
  { id: "playfair", label: "Playfair Display", family: "'Playfair Display', serif", weight: 700, group: "serif" },
  { id: "cormorant", label: "Cormorant", family: "'Cormorant Garamond', serif", weight: 700, group: "serif" },
  { id: "marcellus", label: "Marcellus", family: "'Marcellus', serif", weight: 400, group: "serif" },
  { id: "ebgaramond", label: "EB Garamond", family: "'EB Garamond', serif", weight: 700, group: "serif" },
  { id: "crimson", label: "Crimson Text", family: "'Crimson Text', serif", weight: 700, group: "serif" },
  { id: "lora", label: "Lora", family: "'Lora', serif", weight: 700, group: "serif" },
  { id: "merriweather", label: "Merriweather", family: "'Merriweather', serif", weight: 700, group: "serif" },
  { id: "librecaslon", label: "Libre Caslon", family: "'Libre Caslon Text', serif", weight: 700, group: "serif" },
  { id: "bodoni", label: "Bodoni Moda", family: "'Bodoni Moda', serif", weight: 700, group: "serif" },
  { id: "pacifico", label: "Pacifico", family: "'Pacifico', cursive", weight: 400, group: "script" },
  { id: "sacramento", label: "Sacramento", family: "'Sacramento', cursive", weight: 400, group: "script" },
  { id: "allura", label: "Allura", family: "'Allura', cursive", weight: 400, group: "script" },
  { id: "greatvibes", label: "Great Vibes", family: "'Great Vibes', cursive", weight: 400, group: "script" },
  { id: "italianno", label: "Italianno", family: "'Italianno', cursive", weight: 400, group: "script" },
  { id: "lobster", label: "Lobster", family: "'Lobster', display", weight: 400, group: "script" },
  { id: "caveat", label: "Caveat", family: "'Caveat', cursive", weight: 700, group: "script" },
  { id: "permanent", label: "Permanent Marker", family: "'Permanent Marker', cursive", weight: 400, group: "script" },
  { id: "dancing", label: "Dancing Script", family: "'Dancing Script', cursive", weight: 700, group: "script" },
  { id: "yellowtail", label: "Yellowtail", family: "'Yellowtail', cursive", weight: 400, group: "script" },
];

const COLORS = [
  { id: "blanc", label: { fr: "Blanc" }, hex: "#F4F4F4" },
  { id: "vertfonce", label: { fr: "Vert foncé" }, hex: "#0f5f2a" },
  { id: "vertneon", label: { fr: "Vert néon" }, hex: "#39FF14" },
  { id: "rouge", label: { fr: "Rouge" }, hex: "#c32424" },
  { id: "orange", label: { fr: "Orange" }, hex: "#ff7a18" },
  { id: "bleufonce", label: { fr: "Bleu foncé" }, hex: "#0a2a6e" },
  { id: "bleuciel", label: { fr: "Bleu ciel" }, hex: "#5ec8ff" },
];

// Palette néon (lumineuse, halo coloré sur fond foncé)
const NEON_COLORS = [
  { id: "vert", label: { fr: "Vert", de: "Grün", en: "Green", it: "Verde" }, hex: "#39FF14" },
  { id: "blanc", label: { fr: "Blanc", de: "Weiss", en: "White", it: "Bianco" }, hex: "#FFFFFF" },
  { id: "rose", label: { fr: "Rose", de: "Pink", en: "Pink", it: "Rosa" }, hex: "#FF3DCC" },
  { id: "bleu", label: { fr: "Bleu", de: "Blau", en: "Blue", it: "Blu" }, hex: "#00B8FF" },
  { id: "bleu-fonce", label: { fr: "Bleu foncé", de: "Dunkelblau", en: "Dark blue", it: "Blu scuro" }, hex: "#2E5BFF" },
  { id: "rouge-neon", label: { fr: "Rouge", de: "Rot", en: "Red", it: "Rosso" }, hex: "#FF2D2D" },
  { id: "jaune", label: { fr: "Jaune", de: "Gelb", en: "Yellow", it: "Giallo" }, hex: "#FFD400" },
  { id: "orange-neon", label: { fr: "Orange", de: "Orange", en: "Orange", it: "Arancione" }, hex: "#FF6B00" },
  { id: "violet", label: { fr: "Violet", de: "Violett", en: "Purple", it: "Viola" }, hex: "#B026FF" },
];

// Polices néon (31 fonts script/display) — chargées depuis /app/frontend/public/fonts
const NEON_FONTS = [
  { id: "lejardinsecret",  label: "Le Jardin Secret",    family: "'LeJardinSecret', cursive",    weight: 400, group: "script" },
  { id: "learningcurve",   label: "Learning Curve BV",   family: "'LearningCurve', cursive",     weight: 400, group: "script" },
  { id: "romanticsunrise", label: "Romantic Sunrise",    family: "'RomanticSunrise', cursive",   weight: 400, group: "script" },
  { id: "studynight",      label: "Study Night",         family: "'StudyNight', display",        weight: 400, group: "script" },
  { id: "dklemonyellow",   label: "DK Lemon Yellow Sun", family: "'DKLemon', display",           weight: 400, group: "bold" },
  { id: "domesticpigeon",  label: "Domestic Pigeon",     family: "'DomesticPigeon', sans-serif", weight: 400, group: "script" },
  { id: "milkwater",       label: "Milk Water",          family: "'MilkWater', cursive",         weight: 400, group: "script" },
  { id: "authsignature",   label: "Authentic Signature", family: "'AuthSignature', cursive",     weight: 400, group: "script" },
  { id: "pillowtalk",      label: "Pillow Talk",         family: "'PillowTalk', cursive",        weight: 400, group: "script" },
  { id: "angelface",       label: "Angelface",           family: "'Angelface', cursive",         weight: 400, group: "script" },
  { id: "april",           label: "April",               family: "'AprilFont', cursive",         weight: 400, group: "script" },
  { id: "catalona",        label: "Catalona",            family: "'Catalona', cursive",          weight: 400, group: "script" },
  { id: "kgwildways",      label: "KG Wildways",         family: "'KGWildways', cursive",        weight: 400, group: "bold" },
  { id: "orionsans",       label: "212 Orion Sans",      family: "'OrionSans', sans-serif",      weight: 400, group: "bold" },
  { id: "whitenice",       label: "Whitenice",           family: "'Whitenice', cursive",         weight: 400, group: "script" },
  { id: "notetoselfie",    label: "Note to Selfie",      family: "'NoteToSelfie', cursive",      weight: 400, group: "script" },
  { id: "california",      label: "California",          family: "'CaliforniaFont', cursive",    weight: 400, group: "script" },
  { id: "simplebeach",     label: "Simple Beach",        family: "'SimpleBeach', cursive",       weight: 400, group: "script" },
  { id: "absadventure",    label: "Absolute Adventure",  family: "'AbsoluteAdventure', display", weight: 400, group: "bold" },
  { id: "caviardreams",    label: "Caviar Dreams",       family: "'CaviarDreams', sans-serif",   weight: 400, group: "script" },
  { id: "neouthin",        label: "Neou Thin",           family: "'NeouThin', sans-serif",       weight: 100, group: "script" },
  { id: "yeahyeahyeah",    label: "Yeah Yeah Yeah",      family: "'YeahYeahYeah', cursive",      weight: 400, group: "script" },
  { id: "housesignsans",   label: "House Signature Sans",family: "'HouseSignSans', sans-serif",  weight: 400, group: "script" },
  { id: "brilo",           label: "Brilo",               family: "'Brilo', cursive",             weight: 400, group: "script" },
  { id: "mladen",          label: "Mladen",              family: "'Mladen', cursive",            weight: 400, group: "script" },
  { id: "grostick",        label: "Grostick",            family: "'Grostick', display",          weight: 400, group: "bold" },
  { id: "bondoluopeek",    label: "Bondoluo Peek",       family: "'BondoluoPeek', display",      weight: 400, group: "bold" },
  { id: "daiichi",         label: "Daiichi",             family: "'Daiichi', sans-serif",        weight: 400, group: "bold" },
  { id: "blackoutsunrise", label: "Blackout Sunrise",    family: "'BlackoutSunrise', display",   weight: 400, group: "bold" },
  { id: "vonique92",       label: "Vonique 92",          family: "'Vonique92', serif",           weight: 400, group: "bold" },
  { id: "zector",          label: "Zector",              family: "'Zector', display",            weight: 400, group: "bold" },
];

// Nuancier RAL — sélection des teintes les plus courantes pour la signalétique pharmacie
const RAL_COLORS = [
  { id: "RAL9010", label: "9010 Blanc pur", hex: "#F1ECE2" },
  { id: "RAL9016", label: "9016 Blanc trafic", hex: "#F6F6F6" },
  { id: "RAL9005", label: "9005 Noir foncé", hex: "#0A0A0A" },
  { id: "RAL7016", label: "7016 Gris anthracite", hex: "#293133" },
  { id: "RAL7035", label: "7035 Gris clair", hex: "#D7D7D7" },
  { id: "RAL9006", label: "9006 Alu blanc", hex: "#A5A8A6" },
  { id: "RAL9007", label: "9007 Alu gris", hex: "#8F8F8C" },
  { id: "RAL3020", label: "3020 Rouge trafic", hex: "#C7110F" },
  { id: "RAL3000", label: "3000 Rouge feu", hex: "#AB2524" },
  { id: "RAL3004", label: "3004 Rouge pourpre", hex: "#6D1F1F" },
  { id: "RAL2004", label: "2004 Orange pur", hex: "#E15501" },
  { id: "RAL2008", label: "2008 Orange vif", hex: "#EB6C2B" },
  { id: "RAL1023", label: "1023 Jaune trafic", hex: "#F8AD15" },
  { id: "RAL1018", label: "1018 Jaune zinc", hex: "#F9BF44" },
  { id: "RAL1015", label: "1015 Ivoire clair", hex: "#E6D2B0" },
  { id: "RAL6029", label: "6029 Vert menthe", hex: "#006534" },
  { id: "RAL6018", label: "6018 Vert jaune", hex: "#57A639" },
  { id: "RAL6005", label: "6005 Vert mousse", hex: "#114232" },
  { id: "RAL6024", label: "6024 Vert trafic", hex: "#0E7C3A" },
  { id: "RAL5010", label: "5010 Bleu gentiane", hex: "#0E294B" },
  { id: "RAL5012", label: "5012 Bleu clair", hex: "#2284C6" },
  { id: "RAL5015", label: "5015 Bleu ciel", hex: "#1B7CB0" },
  { id: "RAL5002", label: "5002 Bleu outremer", hex: "#1F2F66" },
  { id: "RAL4006", label: "4006 Pourpre trafic", hex: "#91376F" },
  { id: "RAL4005", label: "4005 Lilas bleu", hex: "#6D597A" },
  { id: "RAL8011", label: "8011 Brun noisette", hex: "#5A3A29" },
  { id: "RAL8017", label: "8017 Brun chocolat", hex: "#44322D" },
];

// Couleurs d'éclairage LED pour le rétro-éclairage (option commune à tous les types d'enseigne)
const LIGHT_COLORS = [
  { id: "warm3000", label: { fr: "Blanc 3000K" }, hex: "#FFD18A" },
  { id: "cool6500", label: { fr: "Blanc 6500K" }, hex: "#EAF6FF" },
  { id: "blue", label: { fr: "Bleu" }, hex: "#00B6FF" },
  { id: "red", label: { fr: "Rouge" }, hex: "#FF1F2D" },
  { id: "green", label: { fr: "Vert" }, hex: "#22FF00" },
  { id: "rvb", label: { fr: "RVB multicolore" }, hex: "#ff00ff", isMulti: true },
];

// Finitions inox 3D — chacune avec un dégradé CSS qui s'applique en background-clip text
const INOX_FINISHES = [
  // ✨ POLI MIROIR — dégradé multi-stops avec specular highlight très marqué
  // pour un effet "chrome" / acier poli : pic blanc franc en haut, reflet
  // sombre central, rebond lumineux bas. Empilement : highlight radial +
  // dégradé miroir + bande de réflexion fine.
  { id: "inox-poli", label: { fr: "Inox poli miroir" }, base: "#a8a8a8",
    gradient: [
      "radial-gradient(120% 55% at 50% 0%, rgba(255,255,255,0.7), rgba(255,255,255,0) 65%)",
      "linear-gradient(180deg, #ffffff 0%, #f6f6f6 6%, #c8c8c8 22%, #6b6b6b 48%, #3b3b3b 52%, #6b6b6b 58%, #c8c8c8 78%, #f0f0f0 94%, #ffffff 100%)",
    ].join(", ") },
  // 🪵 BROSSÉ — texture anisotrope avec brossage vertical fin (steps de 1px)
  // et dégradé horizontal plus doux que le poli pour un fini mat satiné.
  { id: "inox-brosse", label: { fr: "Inox brossé" }, base: "#9a9a9a",
    gradient: [
      "repeating-linear-gradient(90deg, rgba(255,255,255,0.20) 0px, rgba(255,255,255,0) 1px, rgba(0,0,0,0.12) 2px, rgba(255,255,255,0) 3px)",
      "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(0,0,0,0.10) 50%, rgba(255,255,255,0.18) 100%)",
      "linear-gradient(180deg, #e4e4e4 0%, #c2c2c2 30%, #8a8a8a 55%, #b0b0b0 80%, #d4d4d4 100%)",
    ].join(", ") },
  // ✨ GOLD BRILLANT — même technique que poli miroir mais teintes or chaudes.
  { id: "gold-brillant", label: { fr: "Gold brillant" }, base: "#d4a13a",
    gradient: [
      "radial-gradient(120% 55% at 50% 0%, rgba(255,240,200,0.75), rgba(255,240,200,0) 65%)",
      "linear-gradient(180deg, #FFF3C9 0%, #FFE08A 8%, #E0A938 24%, #8C5A0A 48%, #5B3700 52%, #8C5A0A 58%, #E0A938 78%, #FFD45A 94%, #FFF3C9 100%)",
    ].join(", ") },
  // 🪵 GOLD BROSSÉ — brossage doré fin + base or satinée.
  { id: "gold-brosse", label: { fr: "Gold brossé" }, base: "#b58a35",
    gradient: [
      "repeating-linear-gradient(90deg, rgba(255,235,180,0.18) 0px, rgba(0,0,0,0) 1px, rgba(80,50,0,0.16) 2px, rgba(0,0,0,0) 3px)",
      "linear-gradient(180deg, rgba(255,235,180,0.20) 0%, rgba(80,50,0,0.12) 50%, rgba(255,235,180,0.20) 100%)",
      "linear-gradient(180deg, #E9C575 0%, #C99D3D 30%, #8A6112 55%, #B98F2E 80%, #E0BB6A 100%)",
    ].join(", ") },
  // ✨ CUIVRE POLI — chrome cuivré avec specular chaud.
  { id: "cuivre-poli", label: { fr: "Cuivre poli" }, base: "#b87333",
    gradient: [
      "radial-gradient(120% 55% at 50% 0%, rgba(255,210,170,0.75), rgba(255,210,170,0) 65%)",
      "linear-gradient(180deg, #FFE0C2 0%, #F2A877 8%, #C57238 24%, #6F3A12 48%, #3F1F05 52%, #6F3A12 58%, #C57238 78%, #F0A270 94%, #FFE0C2 100%)",
    ].join(", ") },
  // 🪵 CUIVRE BROSSÉ — brossage cuivré + base satinée.
  { id: "cuivre-brosse", label: { fr: "Cuivre brossé" }, base: "#a06b3a",
    gradient: [
      "repeating-linear-gradient(90deg, rgba(255,205,160,0.18) 0px, rgba(0,0,0,0) 1px, rgba(70,30,5,0.16) 2px, rgba(0,0,0,0) 3px)",
      "linear-gradient(180deg, rgba(255,205,160,0.20) 0%, rgba(70,30,5,0.12) 50%, rgba(255,205,160,0.20) 100%)",
      "linear-gradient(180deg, #DDA075 0%, #B47148 30%, #774524 55%, #A66B43 80%, #D89B73 100%)",
    ].join(", ") },
];

// Suppléments retirés sur demande utilisateur — remplacés par une unique option "Rétro-éclairage"

// Châssis (cadre alu) — finitions disponibles uniquement pour signType="chassis"
// La silhouette PNG est utilisée comme mask CSS pour permettre la colorisation dynamique
const CHASSIS_PNG = "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/y02nvsss_image.png";
const CHASSIS_FRAMES = [
  {
    id: "blanc",
    label: { fr: "Blanc", de: "Weiss", en: "White", it: "Bianco" },
    swatch: "#f5f5f5",
    bar: "linear-gradient(180deg, #ffffff 0%, #ededed 50%, #c8c8c8 100%)",
    shadow: "0 1px 0 rgba(255,255,255,0.6) inset, 0 0 0 1px rgba(0,0,0,0.15)",
  },
  {
    id: "gris-alu",
    label: { fr: "Gris alu", de: "Aluminiumgrau", en: "Aluminum grey", it: "Grigio alluminio" },
    swatch: "#a3a3a3",
    bar: "linear-gradient(180deg, #c4c4c4 0%, #8e8e8e 50%, #5e5e5e 100%)",
    shadow: "0 1px 0 rgba(255,255,255,0.35) inset, 0 0 0 1px rgba(0,0,0,0.35)",
  },
  {
    id: "noir",
    label: { fr: "Noir", de: "Schwarz", en: "Black", it: "Nero" },
    swatch: "#1a1a1a",
    bar: "linear-gradient(180deg, #303030 0%, #111111 50%, #000000 100%)",
    shadow: "0 1px 0 rgba(255,255,255,0.06) inset, 0 0 0 1px rgba(0,0,0,0.6)",
  },
  {
    id: "chrome",
    label: { fr: "Chrome", de: "Chrom", en: "Chrome", it: "Cromo" },
    swatch: "linear-gradient(135deg, #ffffff 0%, #c8c8c8 25%, #6e6e6e 50%, #d6d6d6 75%, #ffffff 100%)",
    bar: "linear-gradient(180deg, #f5f5f5 0%, #ffffff 20%, #9a9a9a 50%, #d8d8d8 80%, #6a6a6a 100%)",
    shadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 -1px 0 rgba(0,0,0,0.35) inset, 0 0 0 1px rgba(0,0,0,0.35)",
  },
];

const SIZES = [
  { id: "h15", label: "h. 15 cm" },
  { id: "h25", label: "h. 25 cm" },
  { id: "h40", label: "h. 40 cm" },
  { id: "h60", label: "h. 60 cm" },
  { id: "custom", label: { fr: "Sur-mesure" } },
];

const DEFAULT_Y_BY_SIGNTYPE = {
  boitier: 22,
  retro: 36,
  inox: 50,
  chassis: 64,
  neon: 28,
};

const newLine = (i = 1, signType = "boitier") => ({
  id: `L${Date.now()}${i}`,
  text: "",
  fontId: signType === "neon"
    ? (i === 1 ? "lejardinsecret" : "authsignature")
    : (i === 1 ? "bebas" : "cinzel"),
  colorId: signType === "neon"
    ? "vert"
    : signType === "retro"
      ? RAL_COLORS[0].id
      : signType === "inox"
        ? INOX_FINISHES[0].id
        : (i === 1 ? "vertfonce" : "blanc"),
  pos: { x: 50, y: (DEFAULT_Y_BY_SIGNTYPE[signType] || 28) + (i - 1) * (signType === "neon" ? 18 : 10) },
  scale: 1,
  // Taille par ligne (la sélection h.40 cm / h.60 cm / sur-mesure n'affecte
  // que la ligne active). Avant cette version la taille était globale ; le
  // déplacer au niveau de chaque ligne permet de mixer (ex: titre h.60 +
  // sous-titre h.25 sur la même façade).
  sizeId: "h40",
  customHeightCm: 40,
  // Largeur sur-mesure (cm) — utilisée UNIQUEMENT quand `sizeId === "custom"`.
  // Si null/0 : la largeur est calculée automatiquement à partir du texte.
  // Permet à l'utilisateur de fixer la longueur exacte de la ligne / du
  // châssis indépendamment du nombre de lettres saisi.
  customWidthCm: null,
});

// Convertit le sizeId+customHeightCm d'une ligne en (baseSizePx, heightCm).
// Mêmes barèmes que dans la version "globale" précédente.
function getLineSizing(line) {
  const sizeId = line?.sizeId || "h40";
  const customHeightCm = line?.customHeightCm || 40;
  const baseSizePx = sizeId === "h15" ? 22
    : sizeId === "h25" ? 34
    : sizeId === "h60" ? 60
    : sizeId === "custom" ? Math.max(8, Math.round((customHeightCm || 40) * (44 / 40)))
    : 44;
  const heightCm = sizeId === "h15" ? 15
    : sizeId === "h25" ? 25
    : sizeId === "h40" ? 40
    : sizeId === "h60" ? 60
    : Math.max(5, customHeightCm || 40);
  return { baseSizePx, heightCm };
}

export default function BoxLetterConfigurator({ value, onChange, signType = "boitier", productSlug }) {
  const { lang } = useI18n();
  const { reset: resetComposition } = useComposition();
  const previewRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragRef = useRef({ active: false, lineId: null, mode: "move", startY: 0, startScale: 1 });
  const lineRefs = useRef({});
  const [textMetrics, setTextMetrics] = useState(null);

  // multi-lignes
  const [lines, setLines] = useState(value?.lines?.length ? value.lines : [newLine(1, signType)]);
  const [activeLineId, setActiveLineId] = useState(value?.activeLineId || (value?.lines?.[0]?.id) || lines[0].id);
  const activeLine = lines.find((l) => l.id === activeLineId) || lines[0];

  // Type d'enseigne — piloté par la prop (boitier par défaut, retro pour la page lettre-retro-eclaire)
  // Taille par ligne — les valeurs ci-dessous se lisent dynamiquement sur
  // la ligne active uniquement (cf. activeSizeId / activeCustomHeightCm).
  // L'ancien state global sizeId/customHeightCm a été retiré : chaque ligne
  // porte désormais son propre `sizeId` et `customHeightCm` (cf. newLine).
  const activeSizeId = activeLine?.sizeId || "h40";
  const activeCustomHeightCm = activeLine?.customHeightCm || 40;
  const activeCustomWidthCm = activeLine?.customWidthCm || null;
  const setActiveSizeId = (id) => updateLine(activeLineId, { sizeId: id });
  const setActiveCustomHeightCm = (cm) => updateLine(activeLineId, { customHeightCm: cm });
  const setActiveCustomWidthCm = (cm) => updateLine(activeLineId, { customWidthCm: cm });
  const [sceneId, setSceneId] = useState(value?.sceneId || "stone-chalet");
  const [customImage, setCustomImage] = useState(value?.customImage || null);
  const [mode, setMode] = useState(value?.mode || "day");
  const [interactionMode, setInteractionMode] = useState("move"); // move | resize

  // Mobile : force la scène "bardage blanc" et masque le sélecteur
  const isMobile = useIsMobile();
  const effectiveSceneId = isMobile ? "white-bardage" : sceneId;
  const effectiveCustomImage = isMobile ? null : customImage;
  // Couleur d'éclairage (commune à toutes les lignes)
  const [lightColorId, setLightColorId] = useState(value?.lightColorId || "warm3000");
  // Option "Rétro-éclairage" — toujours actif sur le produit retro, retiré du
  // produit "lettre-boitier-alu" (boitier alu sans rétro-éclairage), optionnel
  // sur inox 3D et châssis. Pas applicable au néon (le texte est la lumière).
  const [hasBacklight, setHasBacklight] = useState(
    signType === "boitier" || signType === "neon"
      ? false
      : value?.hasBacklight !== undefined ? value.hasBacklight : signType === "retro"
  );
  // Couleur du châssis (signType="chassis" uniquement)
  const [chassisFrameId, setChassisFrameId] = useState(value?.chassisFrameId || "gris-alu");
  const chassisFrame = CHASSIS_FRAMES.find((f) => f.id === chassisFrameId) || CHASSIS_FRAMES[1];

  // Palette de couleurs des lettres selon le type d'enseigne
  const PALETTE = signType === "neon"
    ? NEON_COLORS
    : signType === "retro" ? RAL_COLORS : signType === "inox" ? INOX_FINISHES : COLORS;
  // Liste de polices : néon utilise les polices script/display dédiées
  const FONT_LIST = signType === "neon" ? NEON_FONTS : FONTS;
  const lightColor = LIGHT_COLORS.find((l) => l.id === lightColorId) || LIGHT_COLORS[0];

  const scene = effectiveCustomImage
    ? { id: "custom", label: { fr: "Ma photo" }, image: effectiveCustomImage, signTop: 40 }
    : SCENES.find((s) => s.id === effectiveSceneId) || SCENES[0];

  const activeSize = SIZES.find((s) => s.id === activeSizeId) || SIZES[2];

  // propagate config — placé APRÈS `dimensions` plus bas dans le fichier (TDZ).

  // ---------- helpers lignes ----------
  const updateLine = (id, patch) =>
    setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const addLine = () => {
    if (lines.length >= 5) return;
    const l = newLine(lines.length + 1, signType);
    setLines((ls) => [...ls, l]);
    setActiveLineId(l.id);
  };
  const removeLine = (id) => {
    if (lines.length <= 1) {
      // Dernière ligne : on la remplace par une nouvelle ligne vierge plutôt
      // que d'empêcher la suppression. Toutes les persos de l'utilisateur
      // sont effacées mais le configurateur reste utilisable.
      const fresh = newLine(1, signType);
      setLines([fresh]);
      setActiveLineId(fresh.id);
      return;
    }
    const remaining = lines.filter((l) => l.id !== id);
    setLines(remaining);
    if (activeLineId === id) setActiveLineId(remaining[0].id);
  };
  // Touche Suppr / Backspace : supprime la ligne active. Plus de garde-fou
  // sur lines.length — removeLine ci-dessus gère le cas "dernière ligne" en
  // la remplaçant par une ligne vierge.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const t = e.target;
      const tag = (t?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
      if (!activeLineId) return;
      e.preventDefault();
      removeLine(activeLineId);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLineId, lines.length, signType]);

  // ---------- drag / resize ----------
  const startInteraction = (e, lineId) => {
    e.stopPropagation();
    e.preventDefault();
    const l = lines.find((x) => x.id === lineId);
    if (!l) return;
    setActiveLineId(lineId);
    dragRef.current = {
      active: true,
      lineId,
      mode: interactionMode,
      startY: (e.touches?.[0]?.clientY ?? e.clientY) || 0,
      startScale: l.scale,
    };
  };

  const onPreviewMove = (e) => {
    const d = dragRef.current;
    if (!d.active || !previewRef.current) return;
    const pt = e.touches?.[0] || e;
    if (d.mode === "move") {
      const rect = previewRef.current.getBoundingClientRect();
      const x = ((pt.clientX - rect.left) / rect.width) * 100;
      const y = ((pt.clientY - rect.top) / rect.height) * 100;
      updateLine(d.lineId, {
        pos: { x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) },
      });
    } else {
      // resize : drag vertical → scale
      const deltaY = d.startY - pt.clientY;
      const nextScale = Math.max(0.4, Math.min(2.5, d.startScale + deltaY / 150));
      updateLine(d.lineId, { scale: nextScale });
    }
  };

  const endInteraction = () => {
    dragRef.current = { ...dragRef.current, active: false };
  };

  // ---------- upload ----------
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setCustomImage(ev.target.result); };
    reader.readAsDataURL(file);
  };
  const removeCustom = () => setCustomImage(null);

  // ---------- styles ----------
  // Pour les presets : on garde les baseSizePx existants (22 / 34 / 44 / 60).
  // Pour le sur-mesure : on convertit la hauteur cm en px à l'échelle 44 px = 40 cm
  // (mêmes proportions que le preset h40). La largeur reste TOUJOURS proportionnelle
  // à la hauteur car on n'agit que sur fontSize → cohérent quel que soit le texte.
  // ⚠️ Chaque ligne possède désormais sa propre taille — la fonction
  // `getLineSizing(line)` renvoie {baseSizePx, heightCm} pour la ligne fournie.
  // La taille de l'aperçu Dimensions correspond à la LIGNE ACTIVE uniquement.
  const { baseSizePx: activeBaseSizePx, heightCm: activeHeightCm } = getLineSizing(activeLine);

  const buildLineStyle = (line) => {
    const font = FONT_LIST.find((f) => f.id === line.fontId) || FONT_LIST[0];
    const color = PALETTE.find((c) => c.id === line.colorId) || PALETTE[0];
    const { baseSizePx } = getLineSizing(line);
    const fontSize = `${baseSizePx * line.scale}px`;
    const base = {
      fontFamily: font.family,
      fontWeight: font.weight,
      letterSpacing: "0.04em",
      fontSize,
      lineHeight: 1.05,
    };
    // Helpers halo (text-shadow ou drop-shadow). Cas spécial: RVB = multi-couches couleurs.
    const haloTextShadow = (strength) => {
      if (lightColor.isMulti) {
        return `0 0 4px rgba(255,255,255,${0.95 * strength}), 0 0 18px rgba(255,0,0,${1 * strength}), 0 0 38px rgba(255,255,0,${0.85 * strength}), 0 0 60px rgba(0,255,0,${0.8 * strength}), 0 0 100px rgba(0,150,255,${0.75 * strength}), 0 0 150px rgba(180,0,255,${0.7 * strength})`;
      }
      const h = lightColor.hex;
      return `0 0 4px rgba(255,255,255,${strength * 0.95}), 0 0 12px ${rgba(h, strength)}, 0 0 28px ${rgba(h, strength)}, 0 0 52px ${rgba(h, strength * 0.95)}, 0 0 86px ${rgba(h, strength * 0.75)}, 0 0 140px ${rgba(h, strength * 0.5)}`;
    };
    const haloDropShadow = (strength) => {
      if (lightColor.isMulti) {
        return `drop-shadow(0 0 4px rgba(255,255,255,${0.9 * strength})) drop-shadow(0 0 14px rgba(255,0,0,${0.9 * strength})) drop-shadow(0 0 30px rgba(255,255,0,${0.75 * strength})) drop-shadow(0 0 52px rgba(0,255,0,${0.7 * strength})) drop-shadow(0 0 78px rgba(0,120,255,${0.65 * strength})) drop-shadow(0 0 120px rgba(180,0,255,${0.55 * strength}))`;
      }
      const h = lightColor.hex;
      return `drop-shadow(0 0 4px rgba(255,255,255,${strength * 0.9})) drop-shadow(0 0 14px ${rgba(h, strength)}) drop-shadow(0 0 32px ${rgba(h, strength)}) drop-shadow(0 0 64px ${rgba(h, strength * 0.85)}) drop-shadow(0 0 110px ${rgba(h, strength * 0.6)})`;
    };

    // NÉON : halo coloré dense (le texte EST la lumière). Pas d'option
    // rétro-éclairage : le néon est intrinsèquement lumineux.
    if (signType === "neon") {
      const strength = mode === "day" ? 0.6 : 1.0;
      const h = color.hex;
      return {
        ...base,
        color: mode === "day" ? color.hex : "#ffffff",
        textShadow: [
          `0 0 ${2 * strength}px #ffffff`,
          `0 0 ${6 * strength}px ${h}`,
          `0 0 ${14 * strength}px ${h}`,
          `0 0 ${28 * strength}px ${h}`,
          `0 0 ${50 * strength}px ${rgba(h, 0.85)}`,
          `0 0 ${80 * strength}px ${rgba(h, 0.6)}`,
        ].join(", "),
      };
    }
    // INOX 3D : rendu métallique via background-clip text + drop-shadow.
    // Les nouveaux dégradés multi-stops (specular + base + brossage) donnent
    // déjà un effet poli miroir / brossé satiné. On renforce :
    //  - L'ombre portée (relief 3D plus marqué en mode jour)
    //  - Le text-stroke noir (lettre détachée de l'arrière-plan, contraste edges)
    //  - Une légère insetation via background-size: 100% 200% sur le poli
    //    pour amplifier la zone sombre centrale.
    if (signType === "inox") {
      const isPoli = /poli|brillant/.test(color.id);
      const dim = mode === "night" ? "brightness(0.55) contrast(1.05)" : "brightness(1.04) contrast(1.05)";
      const baseShadow = mode === "day"
        ? "drop-shadow(0 1px 1px rgba(0,0,0,0.55)) drop-shadow(0 4px 8px rgba(0,0,0,0.4)) drop-shadow(0 12px 22px rgba(0,0,0,0.28))"
        : "drop-shadow(0 1px 1px rgba(0,0,0,0.75)) drop-shadow(0 4px 12px rgba(0,0,0,0.6))";
      const backlightStrength = hasBacklight ? (mode === "day" ? 0.65 : 1.0) : 0;
      const haloFilter = hasBacklight ? ` ${haloDropShadow(backlightStrength)}` : "";
      return {
        ...base,
        backgroundImage: color.gradient,
        backgroundSize: isPoli ? "100% 100%, 100% 130%" : "auto",
        backgroundPosition: isPoli ? "center top, center center" : "center center",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
        WebkitTextFillColor: "transparent",
        filter: `${baseShadow}${haloFilter} ${dim}`,
        WebkitTextStroke: isPoli ? "0.6px rgba(0,0,0,0.55)" : "0.5px rgba(0,0,0,0.45)",
      };
    }
    // Rétro-éclairé : lettres peintes (couleur RAL) avec halo coloré (light) derrière
    if (signType === "retro") {
      const strength = mode === "day" ? 0.55 : 0.95;
      const isLight = isLightHex(color.hex);
      return {
        ...base,
        color: color.hex,
        textShadow: haloTextShadow(strength),
        WebkitTextStroke: isLight ? "0.5px rgba(0,0,0,0.45)" : "0.4px rgba(0,0,0,0.7)",
      };
    }
    // Boîtier : rendu classique (+ halo backlight optionnel si activé)
    const isDay = mode === "day";
    const baseTextShadow = isDay
      ? `1px 2px 0 rgba(0,0,0,0.35), 0 4px 8px rgba(0,0,0,0.35)`
      : `0 0 4px #fff, 0 0 12px ${color.hex}, 0 0 28px ${color.hex}, 0 0 60px ${rgba(color.hex, 0.85)}, 0 0 110px ${rgba(color.hex, 0.5)}`;
    const backlightOnBoitier = hasBacklight ? `, ${haloTextShadow(isDay ? 0.55 : 0.85)}` : "";
    return {
      ...base,
      color: isDay ? color.hex : lighten(color.hex, 0.55),
      textShadow: `${baseTextShadow}${backlightOnBoitier}`,
    };
  };

  const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-3 flex items-center gap-2";

  // stat couleurs / polices / tailles pour la barre d'outils active
  const activeColorHex = (PALETTE.find((c) => c.id === activeLine.colorId) || PALETTE[0]).base
    || (PALETTE.find((c) => c.id === activeLine.colorId) || PALETTE[0]).hex
    || "#888";
  const activeFont = FONT_LIST.find((f) => f.id === activeLine.fontId) || FONT_LIST[0];

  // ---- Mesure DOM des éléments texte (TOUS les types d'enseigne) ----
  // On mesure le rectangle réel rendu pour :
  //  - signType="chassis" : caler précisément le châssis sur le bas réel
  //    des lettres (descendantes incluses).
  //  - Tous les types : calculer la dimension H × L réelle en cm affichée à
  //    l'utilisateur (proportionnelle à la hauteur de lettre sélectionnée).
  useLayoutEffect(() => {
    if (!previewRef.current) {
      setTextMetrics(null);
      return;
    }
    const measure = () => {
      const prev = previewRef.current?.getBoundingClientRect();
      if (!prev) return;
      const visible = lines.filter((l) => l.text);
      if (visible.length === 0) { setTextMetrics(null); return; }
      let minTop = Infinity, maxBottom = -Infinity, minLeft = Infinity, maxRight = -Infinity;
      // Mesure aussi chaque ligne individuellement (clé : line.id) pour pouvoir
      // dessiner UN châssis par ligne (chaque ligne = son propre support).
      const perLine = {};
      for (const l of visible) {
        const el = lineRefs.current[l.id];
        if (!el) continue;
        // Mesure le <span> intérieur (la VRAIE bbox du texte, sans padding du conteneur)
        const inner = el.querySelector("span") || el;
        const r = inner.getBoundingClientRect();
        const top = ((r.top - prev.top) / prev.height) * 100;
        const bottom = ((r.bottom - prev.top) / prev.height) * 100;
        const left = ((r.left - prev.left) / prev.width) * 100;
        const right = ((r.right - prev.left) / prev.width) * 100;
        perLine[l.id] = { top, bottom, left, right };
        if (top < minTop) minTop = top;
        if (bottom > maxBottom) maxBottom = bottom;
        if (left < minLeft) minLeft = left;
        if (right > maxRight) maxRight = right;
      }
      if (minTop === Infinity) { setTextMetrics(null); return; }
      setTextMetrics({
        top: minTop, bottom: maxBottom, left: minLeft, right: maxRight,
        perLine,
        previewWidthPx: prev.width, previewHeightPx: prev.height,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(previewRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signType, lines, mode, sceneId, customImage]);

  // Dimensions H × L de l'enseigne en cm — calculées pour la LIGNE ACTIVE
  // uniquement (qui porte sa propre taille). Pour multi-lignes mixées (h25 +
  // h60 par ex.) l'utilisateur visualise les dimensions de chaque ligne en
  // sélectionnant son chip L1/L2 dans la barre d'outils.
  const dimensions = useMemo(() => {
    if (!textMetrics || !activeLine) return null;
    const el = lineRefs.current[activeLine.id];
    if (!el) return null;
    const inner = el.querySelector("span") || el;
    const r = inner.getBoundingClientRect();
    const prev = previewRef.current?.getBoundingClientRect();
    if (!prev) return null;
    const widthPx = r.width;
    const heightPx = r.height;
    if (heightPx <= 0 || widthPx <= 0) return null;
    const pxToCm = activeHeightCm / activeBaseSizePx;
    return {
      widthCm: Math.round(widthPx * pxToCm),
      heightCm: Math.round(heightPx * pxToCm),
    };
  }, [textMetrics, activeLine, activeHeightCm, activeBaseSizePx]);

  // propagate config — DOIT être déclaré après `dimensions` car le hook
  // dépend de cette variable (sinon TDZ "Cannot access 'dimensions' before initialization").
  // userTouchedRef : démarre TOUJOURS à `false` au montage, même si la page
  // a été configurée précédemment. C'est la clé du fix anti-doublons :
  // sinon, revenir sur une page validée → toute frappe re-propage instantanément
  // et empile des fantômes sur les autres pages.
  const userTouchedRef = useRef(false);
  // Ne marque PAS le configurateur comme "touché" pour la simple frappe au
  // clavier dans le champ texte — sinon chaque caractère propagerait un
  // calque fantôme dans le montage global. L'utilisateur doit faire une
  // action explicite (cliquer une couleur / police / scène / bouton) pour
  // valider sa composition. Filtre par tagName / type.
  const isTypingTarget = (target) => {
    if (!target) return false;
    const tag = (target.tagName || "").toUpperCase();
    if (tag === "TEXTAREA") return true;
    if (tag === "INPUT") {
      const type = (target.type || "text").toLowerCase();
      return type === "text" || type === "search" || type === "email" || type === "tel" || type === "password";
    }
    return false;
  };
  const markTouched = (e) => {
    if (e && isTypingTarget(e.target)) return;
    userTouchedRef.current = true;
  };

  // Construit la config courante (factorisé) — utilisé à la fois par
  // l'effet réactif (sur changement utilisateur) et par l'écoute de
  // l'événement `signapharma:capture-config` (force la remontée même
  // sans interaction préalable, ex: clic direct "Ajouter au panier").
  const buildCurrentConfig = React.useCallback(() => {
    const enrichedLines = lines.map((l) => {
      const f = FONT_LIST.find((x) => x.id === l.fontId) || FONT_LIST[0];
      const c = PALETTE.find((x) => x.id === l.colorId);
      return {
        ...l,
        fontFamily: f.family,
        fontWeight: f.weight,
        colorHex: c?.hex || null,
      };
    });
    return {
      lines: enrichedLines, activeLineId, signType,
      sizeId: activeSizeId,
      sceneId: effectiveCustomImage ? "custom" : effectiveSceneId,
      customImage: effectiveCustomImage, mode,
      hasBacklight,
      lightColorId: hasBacklight ? lightColorId : null,
      lightColor: hasBacklight ? (typeof lightColor.label === "object" ? lightColor.label.fr : lightColor.label) : null,
      size: typeof activeSize.label === "object" ? activeSize.label.fr : activeSize.label,
      customHeightCm: activeSizeId === "custom" ? activeCustomHeightCm : null,
      customWidthCm: activeSizeId === "custom" ? activeCustomWidthCm : null,
      dimensionsCm: dimensions ? { width: dimensions.widthCm, height: dimensions.heightCm } : null,
      chassisFrameId: signType === "chassis" ? chassisFrameId : null,
      chassisFrame: signType === "chassis" ? chassisFrame.label.fr : null,
      _touched: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, activeLineId, signType, activeSizeId, activeCustomHeightCm, activeCustomWidthCm, dimensions, effectiveSceneId, effectiveCustomImage, mode, lightColorId, hasBacklight, chassisFrameId]);

  React.useEffect(() => {
    if (!userTouchedRef.current) return;
    onChange?.(buildCurrentConfig());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, activeLineId, signType, activeSizeId, activeCustomHeightCm, dimensions, sceneId, customImage, mode, lightColorId, hasBacklight, chassisFrameId]);

  // Écoute l'événement global `signapharma:capture-config` pour pousser
  // la config courante AVANT l'ajout au panier — même si l'utilisateur
  // n'a touché à rien (cas : il clique direct "Ajouter au panier").
  // Écrit aussi dans `window._signapharmaConfigCapture[slug]` pour lecture
  // synchrone côté ProductDetail (zero-latence).
  React.useEffect(() => {
    const handler = () => {
      const cfg = buildCurrentConfig();
      onChange?.(cfg);
      if (productSlug) {
        window._signapharmaConfigCapture = window._signapharmaConfigCapture || {};
        window._signapharmaConfigCapture[productSlug] = cfg;
      }
    };
    window.addEventListener("signapharma:capture-config", handler);
    return () => window.removeEventListener("signapharma:capture-config", handler);
  }, [buildCurrentConfig, onChange, productSlug]);

  // ---- Châssis bounds (signType="chassis") ----
  // Spec client (exemple visuel) : le châssis est ENVIRON 2× plus haut
  // que les lettres. Le BAS des lettres TOUCHE le bas du châssis, l'espace
  // au-dessus des lettres correspond à environ une hauteur de lettres.
  const chassisBoundsList = useMemo(() => {
    if (signType !== "chassis" || !textMetrics?.perLine) return [];
    const out = [];
    for (const line of lines) {
      if (!line.text) continue;
      const m = textMetrics.perLine[line.id];
      if (!m) continue;
      const lineHeightPct = m.bottom - m.top;
      // Le châssis fait 2× la hauteur des lettres (1× lettres + 1× espace au-dessus)
      const top = Math.max(0, m.bottom - 2 * lineHeightPct);
      const bottom = Math.min(100, m.bottom);
      const left = Math.max(0, m.left);
      const right = Math.min(100, m.right);
      out.push({
        id: line.id,
        left,
        top,
        width: right - left,
        height: bottom - top,
      });
    }
    return out;
  }, [signType, textMetrics, lines]);

  return (
    <div
      className="space-y-6"
      data-testid="box-letter-configurator"
      onClickCapture={markTouched}
      onPointerDownCapture={markTouched}
    >
      <MobileDesktopHint />
      <ProjectsToolbar />

      {/* ===== Prix temps réel (CHF HT) — affiche TOUS les éléments du
          montage (croix + lettres) avec bouton "Ajouter au panier" par ligne.
          Le composant lit configs depuis CompositionContext et utilise
          notre état local `lines` pour la page courante (zéro lag). */}
      <GlobalPricePanel
        currentSlug={productSlug}
        localCfg={{ lines, mode, sceneId: effectiveSceneId, customImage: effectiveCustomImage }}
      />


      {/* ======== 1. HEADER: JOUR/NUIT (HORS IMAGE) ======== */}
      <div className="grid grid-cols-1 gap-3">
        {/* Jour / Nuit */}
        <div className="flex items-center gap-0 border border-white/10 bg-[#070707]">
          <div className="px-4 py-3 border-r border-white/10">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">{lang === "fr" ? "Ambiance" : "Mood"}</div>
          </div>
          <button type="button" onClick={() => setMode("day")} data-testid="mode-day"
            className={`flex-1 flex items-center justify-center gap-2 py-3 font-mono text-xs uppercase tracking-widest transition-colors ${mode === "day" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <Sun size={14} />{lang === "fr" ? "Jour" : "Day"}
          </button>
          <button type="button" onClick={() => setMode("night")} data-testid="mode-night"
            className={`flex-1 flex items-center justify-center gap-2 py-3 font-mono text-xs uppercase tracking-widest transition-colors ${mode === "night" ? "bg-[#39FF14] text-black" : "text-zinc-300 hover:bg-white/5"}`}>
            <Moon size={14} />{lang === "fr" ? "Nuit" : "Night"}
          </button>
        </div>
      </div>

      {/* ======== 2. PREVIEW + THUMBNAILS ======== */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_140px] gap-4">
        {/* Big preview */}
        <div className="relative">
          {/* Mode déplacer/redimensionner — HORS image (au-dessus) */}
          <div className="mb-2 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{lang === "fr" ? "Outil" : "Tool"} :</span>
            <button type="button" onClick={() => setInteractionMode("move")} data-testid="tool-move"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${interactionMode === "move" ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
              <Move size={12} />{lang === "fr" ? "Déplacer" : "Move"}
            </button>
            <button type="button" onClick={() => setInteractionMode("resize")} data-testid="tool-resize"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${interactionMode === "resize" ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
              <Maximize2 size={12} />{lang === "fr" ? "Redimensionner" : "Resize"}
            </button>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-zinc-500 hidden sm:inline">
              {interactionMode === "move" ? (lang === "fr" ? "Glissez le texte sur l'image" : "Drag text on image") : (lang === "fr" ? "Glissez verticalement pour ajuster la taille" : "Drag vertically to scale")}
            </span>
            <button type="button" data-testid="reset-composition"
              onClick={() => resetComposition()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-500/50 text-red-400 font-mono text-[10px] uppercase tracking-widest hover:bg-red-500/10 hover:border-red-500"
              title={lang === "fr" ? "Vider tout le cache et recommencer" : "Wipe cache and start over"}>
              <RotateCcw size={12} />{lang === "fr" ? "Réinitialiser" : "Reset"}
            </button>
          </div>

          <div
            ref={previewRef}
            onMouseMove={onPreviewMove}
            onMouseUp={endInteraction}
            onMouseLeave={endInteraction}
            onTouchMove={onPreviewMove}
            onTouchEnd={endInteraction}
            className="relative aspect-[4/3] w-full overflow-hidden bg-[#040404] border border-white/10 select-none"
            data-testid="boxletter-preview"
          >
            <img src={scene.image} alt="" draggable={false}
              data-snapshot-hide="true"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ objectPosition: "center bottom", filter: mode === "night" ? "brightness(0.32) saturate(0.85) contrast(1.05)" : "none" }} />
            {mode === "night" && <div data-snapshot-hide="true" className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(0,4,18,0.55), rgba(0,0,8,0.7))" }} />}

            {/* Overlay des éléments des AUTRES catégories (lecture seule).
                Affiche les croix et autres enseignes configurées sur cette
                même façade, sans quitter la page lettres. Clic = navigation. */}
            <div data-snapshot-hide="true">
              <CompositionOverlay currentSlug={productSlug} mode={mode} />
            </div>

            {/* Châssis cadre métallique — UN cadre PAR ligne (chaque ligne =
                un support distinct avec son propre châssis aluminium). */}
            {signType === "chassis" && chassisBoundsList.map((cb) => (
              <div key={cb.id} className="absolute pointer-events-none" data-testid={`chassis-frame-${cb.id}`} style={{
                left: `${cb.left}%`,
                top: `${cb.top}%`,
                width: `${cb.width}%`,
                height: `${cb.height}%`,
                background: chassisFrame.bar,
                WebkitMaskImage: `url(${CHASSIS_PNG})`,
                maskImage: `url(${CHASSIS_PNG})`,
                WebkitMaskSize: "100% 100%",
                maskSize: "100% 100%",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                filter: mode === "night"
                  ? "brightness(0.55) drop-shadow(0 6px 14px rgba(0,0,0,0.55))"
                  : "drop-shadow(0 6px 12px rgba(0,0,0,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.25))",
              }} />
            ))}

            {/* toutes les lignes positionnées individuellement */}
            {lines.map((line) => {
              const isActive = line.id === activeLineId;
              if (!line.text) return null;
              return (
                <div
                  key={line.id}
                  ref={(el) => {
                    if (el) lineRefs.current[line.id] = el;
                    else delete lineRefs.current[line.id];
                  }}
                  onMouseDown={(e) => startInteraction(e, line.id)}
                  onTouchStart={(e) => startInteraction(e, line.id)}
                  className={`absolute flex items-center justify-center text-center whitespace-nowrap ${interactionMode === "resize" ? "cursor-ns-resize" : "cursor-move"}`}
                  style={{
                    left: `${line.pos.x}%`,
                    top: `${line.pos.y}%`,
                    transform: "translate(-50%, -50%)",
                    padding: "6px 10px",
                    outline: isActive ? "1px dashed rgba(57,255,20,0.65)" : "none",
                    outlineOffset: "4px",
                  }}
                  data-testid={`preview-line-${line.id}`}
                >
                  <span style={{ ...buildLineStyle(line), whiteSpace: signType === "neon" ? "pre-line" : "nowrap" }}>{line.text}</span>
                </div>
              );
            })}

            {/* halo nocturne subtil autour de la ligne active */}
            {mode === "night" && activeLine.text && (
              <div className="absolute inset-0 pointer-events-none" style={{
                background: `radial-gradient(ellipse at ${activeLine.pos.x}% ${activeLine.pos.y}%, ${rgba(activeColorHex, 0.22)}, transparent 30%)`,
              }} />
            )}
          </div>
        </div>

        {/* Thumbnails — groupées en 3 catégories : Façade / Angle / Intérieur.
            Sur mobile (<768px), masqué entièrement — scène forcée à "bardage blanc". */}
        <div className="relative lg:min-h-0 hidden md:block">
          <SceneCategoryPicker
            scenes={SCENES}
            selectedId={sceneId}
            onSelect={(id) => { setSceneId(id); setCustomImage(null); }}
            customScene={customImage ? { id: "custom", label: { fr: "Ma photo" }, image: customImage } : null}
            onUploadClick={() => fileInputRef.current?.click()}
            onClearCustom={() => removeCustom()}
            thumbnailTestIdPrefix="boxletter-scene"
          />
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
        </div>
      </div>

      {/* ======== 3. LIGNES / SUPPORTS : CHIPS ======== */}
      <div className="border border-white/10 bg-[#070707]" data-testid="lines-manager">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 flex-wrap">
          <Layers size={12} className="text-[#39FF14]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">{signType === "neon" ? (lang === "fr" ? "Supports" : "Supports") : (lang === "fr" ? "Lignes" : "Lines")}</span>
          <div className="flex items-center gap-1 flex-wrap ml-2">
            {lines.map((l, idx) => {
              const active = l.id === activeLineId;
              const palItem = PALETTE.find((c) => c.id === l.colorId) || PALETTE[0];
              const col = palItem.hex || palItem.base || "#888";
              const chipPrefix = signType === "neon" ? "S" : "L";
              return (
                <button key={l.id} type="button" onClick={() => setActiveLineId(l.id)} data-testid={`line-chip-${idx + 1}`}
                  className={`group inline-flex items-center gap-1.5 pl-2 pr-1 py-1 border font-mono text-[10px] uppercase tracking-widest transition-all ${active ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                  <span className="inline-block w-2.5 h-2.5 border border-black/50" style={{ background: col }} />
                  <span>{chipPrefix}{idx + 1}</span>
                  <span className="max-w-[90px] truncate text-[9px] opacity-70">{l.text || (lang === "fr" ? "vide" : "empty")}</span>
                  {lines.length > 1 && (
                    <span onClick={(e) => { e.stopPropagation(); removeLine(l.id); }} className="ml-1 p-0.5 text-zinc-500 hover:text-red-400 cursor-pointer" title={lang === "fr" ? "Supprimer" : "Remove"}>
                      <X size={10} />
                    </span>
                  )}
                </button>
              );
            })}
            {lines.length < 5 && (
              <button type="button" onClick={addLine} data-testid="add-line"
                className="inline-flex items-center gap-1 px-2 py-1 border border-dashed border-[#39FF14]/50 text-[#39FF14] font-mono text-[10px] uppercase tracking-widest hover:bg-[#39FF14]/10">
                <Plus size={11} />{signType === "neon" ? (lang === "fr" ? "Nouveau support" : "New support") : (lang === "fr" ? "Ligne" : "Line")}
              </button>
            )}
          </div>
        </div>

        {/* ---- Barre d'outils en long pour la ligne active ---- */}
        <div className="p-4 space-y-4">
          {/* Ligne input texte */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14] shrink-0">{signType === "neon" ? "S" : "L"}{lines.findIndex((l) => l.id === activeLineId) + 1}</span>
            {signType === "neon" ? (
              <textarea
                value={activeLine.text}
                onChange={(e) => updateLine(activeLine.id, { text: e.target.value })}
                maxLength={120}
                rows={2}
                placeholder={lang === "fr" ? "Texte (Entrée = nouvelle ligne)" : "Text (Enter = new line)"}
                data-testid="active-line-text"
                className="flex-1 min-w-[240px] bg-[#0a0a0a] border border-white/10 px-4 py-3 font-body text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14] resize-y leading-tight whitespace-pre-wrap"
              />
            ) : (
              <input
                value={activeLine.text}
                onChange={(e) => updateLine(activeLine.id, { text: e.target.value })}
                maxLength={40}
                placeholder={lang === "fr" ? "Texte (majuscules/minuscules autorisées)" : "Text"}
                data-testid="active-line-text"
                className="flex-1 min-w-[240px] bg-[#0a0a0a] border border-white/10 px-4 py-3 font-body text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14]"
              />
            )}
            {/* Scale rapide */}
            <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-2 bg-[#0a0a0a]">
              <Maximize2 size={12} className="text-zinc-500" />
              <input type="range" min={0.4} max={2.5} step={0.05} value={activeLine.scale}
                onChange={(e) => updateLine(activeLine.id, { scale: parseFloat(e.target.value) })}
                data-testid="active-line-scale"
                className="w-32 accent-[#39FF14]" />
              <span className="font-mono text-[10px] text-zinc-400 w-10 text-right">{Math.round(activeLine.scale * 100)}%</span>
            </div>
          </div>

          {/* Barre longue: polices — 3 groupes empilés (sans / serif / script) ---- */}
          <div data-testid="font-toolbar">
            <div className={labelCls}><Type size={12} className="text-[#39FF14]" />{lang === "fr" ? "Police" : "Font"} — <span className="text-zinc-500 lowercase">{activeFont.label}</span></div>
            <div className="space-y-4">
              {(signType === "neon"
                ? [
                    { key: "script", label: "Script" },
                    { key: "bold", label: "Display / Bold" },
                  ]
                : [
                    { key: "sans", label: "Sans serif" },
                    { key: "serif", label: "Serif" },
                    { key: "script", label: "Script" },
                  ]
              ).map((group) => (
                <div key={group.key}>
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#39FF14]/60" />
                    {group.label}
                  </div>
                  <div className={`${group.key === "sans" ? "flex flex-wrap items-center gap-1.5" : "flex flex-wrap items-center gap-1.5"}`}>
                    {FONT_LIST.filter((f) => f.group === group.key).map((f) => {
                      const selected = activeLine.fontId === f.id;
                      return (
                        <button key={f.id} type="button" onClick={() => updateLine(activeLine.id, { fontId: f.id })} data-testid={`font-${f.group}-${f.id}`}
                          className={`shrink-0 px-3 py-2 border text-center whitespace-nowrap ${selected ? "border-[#39FF14] bg-[#39FF14]/5" : "border-white/10 hover:border-white/30"}`}>
                          <span className="block text-base leading-none" style={{ fontFamily: f.family, fontWeight: f.weight, color: selected ? "#39FF14" : "#e4e4e7" }}>{f.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Barre longue: couleurs des lettres (boîtier / RAL retro / inox finitions) ---- */}
          <div data-testid="color-toolbar">
            <div className={labelCls}>
              <Palette size={12} className="text-[#39FF14]" />
              {signType === "retro"
                ? (lang === "fr" ? "Couleur des lettres (nuancier RAL)" : "Letter color (RAL chart)")
                : signType === "inox"
                  ? (lang === "fr" ? "Finition inox 3D" : "Stainless steel finish")
                  : (lang === "fr" ? "Couleur" : "Color")}
            </div>
            {signType === "retro" ? (
              <div className="flex items-stretch gap-1.5 overflow-x-auto pb-2" data-testid="ral-swatches">
                {PALETTE.map((c) => {
                  const selected = activeLine.colorId === c.id;
                  return (
                    <button key={c.id} type="button" onClick={() => updateLine(activeLine.id, { colorId: c.id })} data-testid={`color-${c.id}`}
                      title={c.label}
                      className={`shrink-0 w-[68px] flex flex-col items-center gap-1 p-1.5 border transition-all ${selected ? "border-[#39FF14] bg-[#39FF14]/5" : "border-white/10 hover:border-white/30"}`}>
                      <span className="block w-full h-8 border border-black/40" style={{ background: c.hex }} />
                      <span className={`font-mono text-[8px] leading-tight text-center ${selected ? "text-[#39FF14]" : "text-zinc-400"}`}>{c.label}</span>
                    </button>
                  );
                })}
              </div>
            ) : signType === "inox" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="inox-swatches">
                {PALETTE.map((c) => {
                  const selected = activeLine.colorId === c.id;
                  return (
                    <button key={c.id} type="button" onClick={() => updateLine(activeLine.id, { colorId: c.id })} data-testid={`color-${c.id}`}
                      title={c.label.fr}
                      className={`flex flex-col items-center gap-2 p-2 border transition-all ${selected ? "border-[#39FF14] bg-[#39FF14]/5" : "border-white/10 hover:border-white/30"}`}>
                      <span className="block w-full h-14 border border-black/40 shadow-[inset_0_2px_4px_rgba(0,0,0,0.35)]" style={{ backgroundImage: c.gradient }} />
                      <span className={`font-mono text-[10px] leading-tight text-center uppercase tracking-wider ${selected ? "text-[#39FF14]" : "text-zinc-400"}`}>{c.label.fr}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                {PALETTE.map((c) => {
                  const selected = activeLine.colorId === c.id;
                  return (
                    <button key={c.id} type="button" onClick={() => updateLine(activeLine.id, { colorId: c.id })} data-testid={`color-${c.id}`}
                      title={typeof c.label === "object" ? c.label.fr : c.label}
                      className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest"
                      style={{
                        borderColor: selected ? "#39FF14" : "rgba(255,255,255,0.12)",
                        background: selected ? "rgba(57,255,20,0.08)" : "transparent",
                        color: selected ? "#fff" : "#9ca3af",
                      }}>
                      <span className="w-5 h-5 border border-black/50" style={{ background: c.hex }} />
                      {typeof c.label === "object" ? c.label.fr : c.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Couleur d'éclairage — affichée inline uniquement pour le produit retro (toujours actif) */}
          {signType === "retro" && (
            <div data-testid="light-toolbar">
              <div className={labelCls}>
                <Sun size={12} className="text-[#39FF14]" />
                {lang === "fr" ? "Couleur d'éclairage arrière" : "Backlight color"}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {LIGHT_COLORS.map((l) => {
                  const selected = lightColorId === l.id;
                  const swatchBg = l.isMulti
                    ? "conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)"
                    : l.hex;
                  const swatchShadow = l.isMulti
                    ? "0 0 12px rgba(255,0,255,0.6), 0 0 6px rgba(0,255,255,0.5) inset"
                    : `0 0 12px ${l.hex}, 0 0 4px ${l.hex} inset`;
                  return (
                    <button key={l.id} type="button" onClick={() => setLightColorId(l.id)} data-testid={`light-${l.id}`}
                      title={l.label.fr}
                      className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest"
                      style={{
                        borderColor: selected ? "#39FF14" : "rgba(255,255,255,0.12)",
                        background: selected ? "rgba(57,255,20,0.08)" : "transparent",
                        color: selected ? "#fff" : "#9ca3af",
                      }}>
                      <span className="w-5 h-5 rounded-full border border-black/40" style={{
                        background: swatchBg,
                        boxShadow: swatchShadow,
                      }} />
                      {l.label.fr}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Barre longue: taille ------------------------------------- */}
          <div data-testid="size-toolbar">
            <div className={labelCls}>
              <Ruler size={12} className="text-[#39FF14]" />
              {lang === "fr" ? "Hauteur des lettres" : "Letter height"}
              <span className="ml-2 normal-case font-mono text-[9px] uppercase tracking-widest text-[#39FF14]/80">
                L{Math.max(1, lines.findIndex((l) => l.id === activeLineId) + 1)}
              </span>
              {dimensions && (
                <span
                  className="ml-2 normal-case text-zinc-400"
                  data-testid="sign-dimensions"
                >
                  — {lang === "fr" ? "dimensions ligne active" : "active line size"} :{" "}
                  <span className="text-[#39FF14] font-mono">
                    {dimensions.heightCm} × {dimensions.widthCm} cm
                  </span>
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {SIZES.map((s) => {
                const selected = activeSizeId === s.id;
                const labelStr = typeof s.label === "object" ? s.label.fr : s.label;
                return (
                  <button key={s.id} type="button" onClick={() => setActiveSizeId(s.id)} data-testid={`size-${s.id}`}
                    className={`px-4 py-2.5 font-mono text-xs uppercase tracking-wider border ${selected ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                    {labelStr}
                  </button>
                );
              })}
            </div>
            {/* Input "sur-mesure" — Hauteur ET Longueur. Les deux dimensions
                sont LIÉES proportionnellement par défaut : changer la hauteur
                recalcule automatiquement la longueur (et vice-versa) selon
                le ratio naturel `nb_lettres × hauteur × 0.55`. L'utilisateur
                peut décrocher le verrou pour les saisir indépendamment. */}
            {activeSizeId === "custom" && (() => {
              const activeLetters = [...String(activeLine?.text || "")].filter((c) => /[^\s]/.test(c)).length || 1;
              const proportionalLink = activeLine?.proportionalLink !== false; // default true
              const autoWidthCm = Math.round(activeLetters * activeCustomHeightCm * 0.55);
              const displayedWidthCm = proportionalLink
                ? autoWidthCm
                : (activeCustomWidthCm ?? autoWidthCm);

              const onHeightChange = (v) => {
                if (proportionalLink) {
                  setActiveCustomHeightCm(v);
                  // longueur recalculée par autoWidthCm (rien à stocker)
                  setActiveCustomWidthCm(null);
                } else {
                  setActiveCustomHeightCm(v);
                }
              };
              const onWidthChange = (v) => {
                if (proportionalLink) {
                  // Inverse : on déduit la hauteur depuis la longueur
                  // h = w / (letters × 0.55)
                  const newH = Math.max(5, Math.round(v / (activeLetters * 0.55)));
                  setActiveCustomHeightCm(newH);
                  setActiveCustomWidthCm(null);
                } else {
                  setActiveCustomWidthCm(v || null);
                }
              };
              const toggleProportionalLink = () => {
                updateLine(activeLineId, { proportionalLink: !proportionalLink });
                if (proportionalLink) {
                  // On vient de DÉVERROUILLER → initialise la longueur à l'auto courante
                  setActiveCustomWidthCm(autoWidthCm);
                } else {
                  // On VERROUILLE → on remet en auto
                  setActiveCustomWidthCm(null);
                }
              };

              return (
                <div className="mt-3 space-y-3" data-testid="size-custom-input-block">
                  <div className="flex items-center gap-3 border border-[#39FF14]/30 bg-[#39FF14]/[0.05] px-4 py-3 flex-wrap">
                    <Ruler size={14} className="text-[#39FF14]" />
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                      {lang === "fr" ? "Hauteur" : "Height"}
                    </label>
                    <input
                      type="number"
                      min={5}
                      max={200}
                      step={1}
                      value={activeCustomHeightCm}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v)) onHeightChange(Math.min(200, Math.max(5, v)));
                      }}
                      data-testid="size-custom-input"
                      className="w-24 bg-black/40 border border-white/15 px-3 py-2 font-mono text-sm text-[#39FF14] focus:outline-none focus:border-[#39FF14]"
                    />
                    <span className="font-mono text-xs text-zinc-400">cm</span>

                    <button
                      type="button"
                      onClick={toggleProportionalLink}
                      data-testid="proportional-link-toggle"
                      title={lang === "fr" ? "Lier hauteur ↔ longueur (proportionnel)" : "Link H ↔ L (proportional)"}
                      className={`inline-flex items-center justify-center w-8 h-8 border transition-colors ${proportionalLink ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/15 text-zinc-500 hover:border-white/30"}`}
                    >
                      {proportionalLink ? <Link2 size={13} /> : <Link2Off size={13} />}
                    </button>

                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                      {lang === "fr" ? "Longueur" : "Length"}
                    </label>
                    <input
                      type="number"
                      min={5}
                      max={1000}
                      step={1}
                      value={displayedWidthCm}
                      readOnly={proportionalLink}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v)) onWidthChange(Math.min(1000, Math.max(5, v)));
                      }}
                      data-testid="size-custom-width-input"
                      className={`w-24 bg-black/40 border border-white/15 px-3 py-2 font-mono text-sm focus:outline-none focus:border-[#39FF14] ${proportionalLink ? "text-zinc-500" : "text-[#39FF14]"}`}
                    />
                    <span className="font-mono text-xs text-zinc-400">cm</span>
                  </div>
                  <p className="font-mono text-[10px] text-zinc-500 px-1">
                    {proportionalLink
                      ? (lang === "fr"
                          ? "↳ Dimensions liées : changer l'une recalcule l'autre proportionnellement. Cliquez sur l'icône pour les dissocier."
                          : "↳ Linked: changing one recomputes the other. Click the icon to unlink.")
                      : (lang === "fr"
                          ? "↳ Hauteur et longueur indépendantes. Cliquez sur l'icône pour les ré-associer proportionnellement."
                          : "↳ H and L independent. Click the icon to re-link them.")}
                  </p>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ======== 3.5 OPTION : COULEUR DU CHÂSSIS (chassis uniquement) ======== */}
      {signType === "chassis" && (
        <div className="border border-white/10 bg-[#070707] p-4" data-testid="chassis-color-block">
          <div className={labelCls}>
            <Palette size={12} className="text-[#39FF14]" />
            {lang === "fr" ? "Couleur du châssis" : "Chassis color"} — <span className="text-zinc-500 lowercase">{chassisFrame.label.fr}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {CHASSIS_FRAMES.map((f) => {
              const selected = chassisFrameId === f.id;
              const labelStr = typeof f.label === "object" ? f.label[lang] || f.label.fr : f.label;
              return (
                <button key={f.id} type="button" onClick={() => setChassisFrameId(f.id)} data-testid={`chassis-frame-${f.id}`}
                  className={`flex items-center gap-2 px-3 py-2 border font-mono text-[10px] uppercase tracking-widest text-left transition-all ${selected ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>
                  <span className="inline-block w-6 h-6 border border-black/40 shrink-0" style={{
                    background: f.swatch,
                    boxShadow: f.shadow,
                  }} />
                  <span className="truncate">{labelStr}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ======== 4. OPTION : RÉTRO-ÉCLAIRAGE (inox 3D et châssis uniquement).
           Retiré du produit "lettre-boitier-alu" (signType="boitier") à la
           demande utilisateur : les lettres boîtier alu n'ont pas d'option
           rétro-éclairage — il faut prendre une lettre rétro-éclairée dédiée. */}
      {signType !== "retro" && signType !== "boitier" && signType !== "neon" && (
        <div className="border border-white/10 bg-[#070707]" data-testid="backlight-option-block">
          <button type="button"
            onClick={() => setHasBacklight((b) => !b)}
            data-testid="toggle-backlight"
            className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-white/[0.02] transition-colors">
            <span className={`w-5 h-5 border-2 flex items-center justify-center shrink-0 ${hasBacklight ? "border-[#39FF14] bg-[#39FF14]" : "border-white/30"}`}>
              {hasBacklight && <span className="text-black text-xs font-bold leading-none">✓</span>}
            </span>
            <span className="flex-1">
              <span className={`block font-mono text-[11px] uppercase tracking-widest ${hasBacklight ? "text-[#39FF14]" : "text-zinc-300"}`}>
                {lang === "fr" ? "Rétro-éclairage en option" : "Backlight option"}
              </span>
              <span className="block font-body text-xs text-zinc-500 mt-1">
                {lang === "fr"
                  ? "Ajoute un halo lumineux LED derrière les lettres. Couleur d'éclairage au choix ci-dessous."
                  : "Adds an LED back-light halo. Pick the light color below."}
              </span>
            </span>
            {hasBacklight && (
              <span className="font-mono text-[10px] text-[#39FF14] shrink-0">
                {(LIGHT_COLORS.find((l) => l.id === lightColorId) || LIGHT_COLORS[0]).label.fr}
              </span>
            )}
          </button>

          {hasBacklight && (
            <div className="px-4 pb-4 pt-1 border-t border-white/5" data-testid="backlight-colors">
              <div className="flex items-center gap-2 flex-wrap">
                {LIGHT_COLORS.map((l) => {
                  const selected = lightColorId === l.id;
                  const swatchBg = l.isMulti
                    ? "conic-gradient(from 0deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)"
                    : l.hex;
                  const swatchShadow = l.isMulti
                    ? "0 0 12px rgba(255,0,255,0.6), 0 0 6px rgba(0,255,255,0.5) inset"
                    : `0 0 12px ${l.hex}, 0 0 4px ${l.hex} inset`;
                  return (
                    <button key={l.id} type="button" onClick={() => setLightColorId(l.id)} data-testid={`light-${l.id}`}
                      title={l.label.fr}
                      className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest"
                      style={{
                        borderColor: selected ? "#39FF14" : "rgba(255,255,255,0.12)",
                        background: selected ? "rgba(57,255,20,0.08)" : "transparent",
                        color: selected ? "#fff" : "#9ca3af",
                      }}>
                      <span className="w-5 h-5 rounded-full border border-black/40" style={{
                        background: swatchBg,
                        boxShadow: swatchShadow,
                      }} />
                      {l.label.fr}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function rgba(hex, a) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
function lighten(hex, amount) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return `rgb(${Math.round(r + (255 - r) * amount)}, ${Math.round(g + (255 - g) * amount)}, ${Math.round(b + (255 - b) * amount)})`;
}
function isLightHex(hex) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  // Luma approximation (Rec. 709)
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luma > 180;
}

